import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Loader2, Tag, ShieldCheck } from "lucide-react";
import { useLang, localized } from "../i18n/LanguageContext";
import useSEO from "../hooks/useSEO";
import api from "../lib/api";
import { fromPerDay } from "../lib/pricing";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const pad = (n) => String(n).padStart(2, "0");
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const Booking = () => {
  const { lang, tr } = useLang();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  useSEO({ title: `${tr.flow.title} — MobiRider`, description: tr.hero.subtitle, lang });

  const [cities, setCities] = useState([]);
  const [products, setProducts] = useState([]);
  const [step, setStep] = useState(1);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoState, setPromoState] = useState(null);

  const tomorrow = new Date(Date.now() + 86400000);
  const later = new Date(Date.now() + 4 * 86400000);

  const [f, setF] = useState({
    city_id: sp.get("city") || "",
    product_id: sp.get("product") || "",
    start: sp.get("start") || `${isoDate(tomorrow)}T10:00`,
    end: sp.get("end") || `${isoDate(later)}T18:00`,
    quantity: 1,
    promo_code: "",
    options: [],
    size: "",
    first_name: "", last_name: "", email: "", phone: "",
    delivery_type: "hotel",
    delivery_details: {},
    return_same: true,
    return_details: {},
    notes: "",
  });

  useEffect(() => {
    api.get("/cities").then((r) => { setCities(r.data); setF((p) => ({ ...p, city_id: p.city_id || r.data[0]?.id })); });
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const cityProducts = useMemo(() => {
    const c = cities.find((x) => x.id === f.city_id);
    if (!c) return products;
    return products.filter((p) => c.product_ids.includes(p.id));
  }, [cities, products, f.city_id]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setDD = (k, v) => setF((p) => ({ ...p, delivery_details: { ...p.delivery_details, [k]: v } }));

  const refreshQuote = async (promo) => {
    if (!f.city_id || !f.product_id) return;
    try {
      const { data } = await api.post("/quote", {
        city_id: f.city_id, product_id: f.product_id, start: f.start, end: f.end,
        quantity: Number(f.quantity), options: f.options,
        promo_code: promo !== undefined ? promo : (promoState === "ok" ? f.promo_code : null),
      });
      setQuote(data);
      return data;
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (step >= 2 && f.product_id) refreshQuote(); }, [step, f.product_id, f.city_id, f.start, f.end, f.quantity]);

  const applyPromo = async () => {
    const q = await refreshQuote(f.promo_code);
    if (q?.promo_valid) { setPromoState("ok"); toast.success(tr.flow.promoOk); }
    else { setPromoState("bad"); toast.error(tr.flow.promoBad); }
  };

  const pay = async () => {
    setLoading(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        ...f, quantity: Number(f.quantity),
        promo_code: promoState === "ok" ? f.promo_code : null,
        lang,
      });
      const { data: checkout } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
      window.location.href = checkout.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setLoading(false);
    }
  };

  const selectedProduct = products.find((p) => p.id === f.product_id);
  const steps = [tr.flow.equipment, tr.flow.address, tr.flow.payment];
  const canNext1 = f.city_id && f.product_id && f.start && f.end;
  const canNext2 = f.first_name && f.last_name && f.email && f.phone;

  const dt = tr.addrTypes;
  const deliveryTypes = ["hotel", "airbnb", "home", "residence", "ehpad", "camping", "cruise", "other"];

  return (
    <div className="max-w-4xl mx-auto px-6 py-10 md:py-14" data-testid="booking-flow">
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-ink tracking-tight text-center">{tr.flow.title}</h1>

      {/* stepper */}
      <div className="flex items-center justify-center gap-2 md:gap-4 my-8">
        {steps.map((s, i) => (
          <React.Fragment key={i}>
            <div className={`flex items-center gap-2 ${step === i + 1 ? "text-brand" : step > i + 1 ? "text-green-600" : "text-slate-400"}`}>
              <span className={`w-10 h-10 rounded-full grid place-items-center font-bold text-lg border-2 ${step === i + 1 ? "border-brand bg-brand text-white" : step > i + 1 ? "border-green-600 bg-green-600 text-white" : "border-slate-300"}`}>
                {step > i + 1 ? <Check className="w-5 h-5" /> : i + 1}
              </span>
              <span className="hidden sm:block font-semibold text-base">{s}</span>
            </div>
            {i < steps.length - 1 && <div className={`w-8 md:w-16 h-0.5 ${step > i + 1 ? "bg-green-600" : "bg-slate-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-md p-6 md:p-8">
        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <label className="block text-lg font-semibold text-ink mb-2">{tr.booking.destination}</label>
              <select data-testid="bf-city" value={f.city_id} onChange={(e) => set("city_id", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg">
                {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-lg font-semibold text-ink mb-2">{tr.booking.delivery}</label>
                <input data-testid="bf-start" type="datetime-local" value={f.start} onChange={(e) => set("start", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-3 text-lg" />
              </div>
              <div>
                <label className="block text-lg font-semibold text-ink mb-2">{tr.booking.pickup}</label>
                <input data-testid="bf-end" type="datetime-local" value={f.end} onChange={(e) => set("end", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-3 text-lg" />
              </div>
            </div>
            <div>
              <label className="block text-lg font-semibold text-ink mb-3">{tr.flow.selectProduct}</label>
              <div className="grid sm:grid-cols-2 gap-3">
                {cityProducts.map((p) => (
                  <button key={p.id} data-testid={`bf-product-${p.category}`} onClick={() => set("product_id", p.id)}
                    className={`flex items-center gap-4 p-3 rounded-2xl border-2 text-left transition-colors ${f.product_id === p.id ? "border-brand bg-brand/5" : "border-slate-200 hover:border-slate-300"}`}>
                    <img src={p.image} alt="" className="w-16 h-16 rounded-xl object-cover bg-slate-50" />
                    <div>
                      <div className="font-semibold text-ink text-base">{localized(p.name, lang)}</div>
                      <div className="text-brand font-bold">{tr.booking.from} {fromPerDay(p)} € {tr.products.perDay}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-lg font-semibold text-ink mb-2">{tr.flow.quantity}</label>
              <input data-testid="bf-qty" type="number" min={1} max={5} value={f.quantity} onChange={(e) => set("quantity", e.target.value)} className="w-28 h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
            </div>
            {selectedProduct?.sizes?.length > 0 && (
              <div>
                <label className="block text-lg font-semibold text-ink mb-2">{tr.flow.size}</label>
                <select data-testid="bf-size" value={f.size} onChange={(e) => set("size", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg">
                  <option value="">—</option>
                  {selectedProduct.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                  <option value="__advise__">{tr.flow.adviseMe}</option>
                </select>
              </div>
            )}
            {selectedProduct?.options?.length > 0 && (
              <div>
                <label className="block text-lg font-semibold text-ink mb-2">{tr.flow.options}</label>
                <div className="space-y-2">
                  {selectedProduct.options.map((o) => {
                    const checked = f.options.includes(o.key);
                    return (
                      <label key={o.key} data-testid={`bf-option-${o.key}`} className={`flex items-center justify-between gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${checked ? "border-brand bg-brand/5" : "border-slate-200 hover:border-slate-300"}`}>
                        <span className="flex items-center gap-3 text-base font-medium text-ink">
                          <input type="checkbox" checked={checked} onChange={(e) => setF((p) => ({ ...p, options: e.target.checked ? [...p.options, o.key] : p.options.filter((k) => k !== o.key) }))} />
                          {(o.label && (o.label[lang] || o.label.fr)) || o.key}
                        </span>
                        <span className="font-bold text-brand">+ {o.price.toFixed(0)} €</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-5">
            <h2 className="font-heading font-bold text-2xl text-ink">{tr.flow.customer}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="block text-base font-semibold text-ink mb-2">{tr.flow.firstName}</label><input data-testid="bf-firstname" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" /></div>
              <div><label className="block text-base font-semibold text-ink mb-2">{tr.flow.lastName}</label><input data-testid="bf-lastname" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" /></div>
              <div><label className="block text-base font-semibold text-ink mb-2">Email</label><input data-testid="bf-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" /></div>
              <div><label className="block text-base font-semibold text-ink mb-2">{tr.flow.phone}</label><input data-testid="bf-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" /></div>
            </div>

            <h2 className="font-heading font-bold text-2xl text-ink pt-2">{tr.flow.deliveryType}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {deliveryTypes.map((k) => (
                <button key={k} data-testid={`bf-addr-${k}`} onClick={() => set("delivery_type", k)}
                  className={`p-3 rounded-xl border-2 font-semibold text-sm transition-colors ${f.delivery_type === k ? "border-brand bg-brand/5 text-brand" : "border-slate-200 text-ink-muted hover:border-slate-300"}`}>
                  {dt[k]}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {f.delivery_type === "hotel" && (<>
                <input data-testid="bf-hotelname" placeholder={tr.flow.hotelName} onChange={(e) => setDD("hotel_name", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
                <input placeholder={tr.flow.bookerName} onChange={(e) => setDD("booker_name", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
                <input data-testid="bf-address" placeholder={tr.flow.addrPlaceholder} onChange={(e) => setDD("address", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
              </>)}
              {f.delivery_type === "cruise" && (<>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input placeholder={tr.flow.port} onChange={(e) => setDD("port", e.target.value)} className="h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
                  <input placeholder={tr.flow.company} onChange={(e) => setDD("company", e.target.value)} className="h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
                  <input placeholder={tr.flow.ship} onChange={(e) => setDD("ship", e.target.value)} className="h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
                  <input placeholder={tr.flow.arrival} onChange={(e) => setDD("arrival", e.target.value)} className="h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
                </div>
              </>)}
              {(f.delivery_type === "ehpad" || f.delivery_type === "residence") && (
                <input placeholder={tr.flow.establishment} onChange={(e) => setDD("establishment", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
              )}
              {["airbnb", "home", "camping", "other", "ehpad", "residence"].includes(f.delivery_type) && (
                <input data-testid="bf-address" placeholder={tr.flow.addrPlaceholder} onChange={(e) => setDD("address", e.target.value)} className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
              )}
              {f.delivery_type === "airbnb" && (
                <textarea placeholder={tr.flow.accessInfo} onChange={(e) => setDD("access", e.target.value)} className="w-full rounded-xl border-2 border-slate-200 focus:border-brand px-4 py-3 text-lg" rows={2} />
              )}
              <textarea data-testid="bf-notes" placeholder={tr.flow.notesLabel} value={f.notes} onChange={(e) => set("notes", e.target.value)} className="w-full rounded-xl border-2 border-slate-200 focus:border-brand px-4 py-3 text-lg" rows={2} />
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && quote && (
          <div className="space-y-5">
            <h2 className="font-heading font-bold text-2xl text-ink">{tr.flow.summary}</h2>
            <div className="bg-cream rounded-2xl p-5 space-y-2 text-lg">
              <div className="flex justify-between"><span className="text-ink-muted">{selectedProduct && localized(selectedProduct.name, lang)} × {f.quantity} · {quote.days} {tr.booking.days}</span><span className="font-semibold">{quote.rental.toFixed(2)} €</span></div>
              <div className="flex justify-between"><span className="text-ink-muted">{tr.flow.deliveryFee}</span><span className="font-semibold">{quote.delivery.toFixed(2)} €</span></div>
              {quote.options?.map((o) => (<div key={o.key} className="flex justify-between"><span className="text-ink-muted">{(o.label && (o.label[lang] || o.label.fr)) || o.key}</span><span className="font-semibold">{o.price.toFixed(2)} €</span></div>))}
              {quote.discount > 0 && <div className="flex justify-between text-green-600"><span>{tr.flow.discount}</span><span className="font-semibold">− {quote.discount.toFixed(2)} €</span></div>}
              <div className="border-t border-slate-200 pt-2 flex justify-between text-xl font-heading font-extrabold text-brand"><span>{tr.flow.total}</span><span>{quote.total.toFixed(2)} €</span></div>
              <div className="flex justify-between text-sm text-ink-muted"><span>{tr.products.deposit}</span><span>{quote.deposit.toFixed(2)} €</span></div>
            </div>

            <div className="flex gap-2">
              <input data-testid="bf-promo" placeholder={tr.flow.promo} value={f.promo_code} onChange={(e) => { set("promo_code", e.target.value); setPromoState(null); }} className="flex-1 h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg" />
              <Button data-testid="bf-apply-promo" onClick={applyPromo} variant="outline" className="h-14 px-6 rounded-xl border-2 font-semibold"><Tag className="w-5 h-5 mr-2" />{tr.flow.apply}</Button>
            </div>

            <div className="bg-brand/5 rounded-2xl p-4 flex items-start gap-3 text-base text-ink-muted">
              <ShieldCheck className="w-6 h-6 text-brand shrink-0" /> {tr.flow.depositNote}
            </div>

            <Button data-testid="bf-pay-btn" onClick={pay} disabled={loading} className="w-full h-16 rounded-full bg-accent2 hover:bg-accent2-hover text-white font-bold text-xl">
              {loading ? <><Loader2 className="w-6 h-6 mr-2 animate-spin" /> {tr.flow.processing}</> : <>{tr.flow.pay} · {quote.total.toFixed(2)} €</>}
            </Button>
          </div>
        )}

        {/* nav */}
        <div className="flex justify-between mt-8">
          <Button variant="outline" disabled={step === 1} onClick={() => setStep((s) => s - 1)} data-testid="bf-back" className="h-14 px-6 rounded-full border-2 font-semibold text-lg"><ChevronLeft className="w-5 h-5 mr-1" />{tr.flow.back}</Button>
          {step < 3 && (
            <Button data-testid="bf-next" onClick={() => setStep((s) => s + 1)} disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)} className="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-lg">{tr.flow.next}<ChevronRight className="w-5 h-5 ml-1" /></Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Booking;
